import React, { Fragment, useCallback, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";
import {
  all,
  always,
  ifElse,
  includes,
  map,
  mergeLeft,
  pipe,
  pluck,
  props,
  reject,
  sort,
  values,
} from "ramda";
import clsx from "clsx";
import { isBlank, notBlank } from "../../../util/ext";

import {
  validateSubmission,
  validateSubmissionField,
  hasError,
  firstError,
  requiredFields,
} from "./uploadValidation";
import {
  getAcceptedFileTypes,
  mapExternalLinksForIngestion,
} from "./uploadUtils";
import { useSelector } from "react-redux";
import { useSnackAlert } from "../../../common/use-snackAlert";
import { MediaContext } from "../../common/media-context";
import { useConfirm } from "../../../common/use-confirm";
import { useUploadUserSubmission } from "./uploadServices";
import {
  getFormattedPurchaseLink,
  setProductIdentifiers,
} from "../../../util/mediaUtil";
import { validateProductIdentifiers } from "../../content/contentValidation";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import FileUpload from "./FileUpload";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MuiDialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";

import Categories from "./Categories";
import ConfirmDialog from "../../../components/ConfirmDialog";
import Contributors from "./Contributors";
import Description from "./Description";
import LoadingIndicator from "../../../components/LoadingIndicator";
import MediaTypeSelect from "./MediaTypeSelect";
import SnackAlert from "../../../components/SnackAlert";
import SubmissionDetails from "./SubmissionDetails";
import SubmissionInfo from "./SubmissionInfo";
import OptionalFields from "./OptionalFields";
import ProductIdentifiers from "../../content/edit/ProductIdentifiers";
import ExternalLinks from "../../common/ExternalLinks";

const styles = theme => ({
  root: {
    padding: "40px 0 0 0",
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    flexWrap: "wrap",
  },

  actions: {
    paddingTop: 20,
    paddingBottom: 70,
  },

  closeButton: {
    color: theme.palette.primary.darker,
    fontWeight: "bolder",
  },

  closeButtonIcon: {
    fontSize: "3.2rem",
  },

  categories: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    gap: 10,
  },

  column: {
    display: "flex",
    flexGrow: 1,
    flexBasis: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    gap: 24,
  },

  columnLeft: {
    paddingRight: 25,
  },

  columnRight: {
    marginTop: 32,
    paddingLeft: 25,
  },

  contributors: {
    paddingTop: 35,
    [theme.breakpoints.up("md")]: {
      paddingLeft: 25,
      paddingTop: 0,
    },
  },

  externalLinks: {
    paddingTop: 35,
    [theme.breakpoints.up("md")]: {
      paddingLeft: 0,
      paddingRight: 25,
    },
  },

  dialogRoot: {
    padding: "0 50px",
  },

  divider: {
    width: "100%",
    margin: theme.spacing(5, 0, 7, 0),
  },

  editPanel: {
    width: "calc(100% - 15px)",
    marginBottom: 30,
  },

  fileUploadContainer: {
    marginTop: 25,
    [theme.breakpoints.up("sm")]: {
      marginTop: 0,
      marginLeft: 25,
    },
  },

  header: {
    margin: 0,
    padding: "40px 0 20px 0",
  },

  info: {
    padding: 0,
  },

  loadingContainer: {
    padding: theme.spacing(2, 5),
    height: "100%",
  },

  productIdentifiers: {
    paddingTop: 35,
  },

  required: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(3),
  },
});

const useStyles = makeStyles(styles);

const DialogTitle = withStyles(styles)(props => {
  const { children, classes, onClose, type, ...other } = props;

  return (
    <MuiDialogTitle className={classes.header} {...other}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid container alignItems="center" item xs={6}>
          <Grid item>
            <Box minWidth={150}>
              <Typography variant="h6" style={{ fontSize: "2.2rem" }}>
                {children}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Grid
          container
          item
          justifyContent="flex-end"
          alignItems="center"
          xs={6}
        >
          {onClose ? (
            <Grid item>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={onClose}
                size="large"
              >
                <CloseIcon className={classes.closeButtonIcon} />
              </IconButton>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    </MuiDialogTitle>
  );
});

// Need to transform contributor to match SubmissionResource.ContributorResource in stip
const mapContributor = contrib => {
  if (contrib.contributorType === "CORPORATION") {
    return {
      corporationName: contrib.fullName,
      type: contrib.contributorType,
      role: contrib.role.id,
    };
  } else {
    return {
      firstName: contrib.firstName,
      middleName: contrib.middleName,
      lastName: contrib.lastName,
      type: contrib.contributorType,
      role: contrib.role.id,
    };
  }
};

// COVER files must be last in list when sent to stip
const mapFiles = pipe(
  props(["submission", "cover"]),
  reject(isBlank),
  sort(a => (a.fileType === "COVER" ? 1 : -1)),
);

const validator = {
  validate: validateSubmission,
  validateField: validateSubmissionField,
  hasError: hasError,
  firstError: firstError,
};

const validateFile = ifElse(
  isBlank,
  always({ file: "A file must be provided" }),
  always({}),
);

//-- CreatorUpload.UploadMetadata in stip
const initialMetadata = {
  ageRange: "",
  audience: "",
  audioType: "",
  categories: [],
  contributors: [],
  description: "",
  descriptionText: "",
  edition: "",
  episode: "",
  isbn: "",
  language: "",
  licenseType: "",
  mediumFormat: "",
  productIdentifiers: [],
  externalLinks: [],
  publicationYear: "",
  publisher: "",
  purchaseLink: {
    title: "",
    url: "",
  },
  rating: "",
  series: "",
  subtitle: "",
  textDirectionality: "",
  title: "",
  type: "",
  vendor: "",
  volume: "",
};

const initialFiles = {
  submission: null,
  cover: null,
};

const UploadMediaDialog = ({ visible, onCancel }) => {
  const classes = useStyles();
  const auth = useSelector(state => state.auth);

  //-- Manage the alert message
  const snackAlertHandler = useSnackAlert();
  const { showAlert } = snackAlertHandler;

  //-- Manage the confirm dialog
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  //-- Set up overall state
  const [submission, setSubmission] = useState(initialMetadata);
  const [errors, setErrors] = useState({});
  const [initialSubmission, setInitialSubmission] = useState(initialMetadata);
  const [loading, setLoading] = useState(false);

  //-- Handle User Upload
  const [fileResources, setFileResources] = useState(initialFiles);
  const uploadUserSubmission = useUploadUserSubmission();
  const handleSave = useCallback(async () => {
    setLoading(true);

    const validation = pipe(
      mergeLeft(validateProductIdentifiers(submission.productIdentifiers)),
      mergeLeft(validateFile(fileResources.submission)),
    )(validateSubmission(submission.type, submission));

    const errors = isBlank(validation) ? {} : validation;
    setErrors(errors);
    if (isBlank(errors) && notBlank(fileResources.submission)) {
      try {
        const publicationDate = notBlank(submission.publicationDate)
          ? submission.publicationDate.toISODate()
          : null;

        const purchaseLink = all(isBlank, values(submission.purchaseLink))
          ? null
          : getFormattedPurchaseLink(submission);

        const externalLinks = mapExternalLinksForIngestion(
          submission.externalLinks,
        );

        const submissionData = pipe(
          mergeLeft({
            publicationDate,
            purchaseLink,
            externalLinks,
          }),
          setProductIdentifiers,
        )(submission);

        const metadata = {
          ...submissionData,
          contributors: map(mapContributor, submission.contributors),
        };

        const files = mapFiles(fileResources);

        const userUpload = {
          user: {
            userId: auth.user.id,
            email: auth.user.username,
          },
          organizationId: auth.currentOrganization.id,
          metadata: reject(isBlank, metadata),
          files: files,
        };

        console.log("Uploading user submission...");
        console.log(userUpload);

        await uploadUserSubmission.mutateAsync({ data: userUpload });

        showConfirm(
          "Your media has been uploaded successfully",
          "Your media is processing and will be available soon. Would you like to upload more?",
          {
            onConfirm: () => {
              setSubmission(initialSubmission);
              setInitialSubmission(initialSubmission);
            },
            onCancel: () => {
              onCancel();
              setSubmission(initialSubmission);
              setInitialSubmission(initialSubmission);
            },
          },
          "Continue Uploading",
          "No, I'm done",
        );
      } catch (err) {
        if (err?.body) {
          if (notBlank(err.body.errors)) {
            showAlert("error", pluck("message", err.body.errors));
          } else {
            // errors from STIP are found at err.body.message
            showAlert("error", [err.body?.message ?? err.message]);
          }
        } else {
          showAlert("error", [err.message]);
        }
      } finally {
        setLoading(false);
      }
    } else {
      showAlert("error", [
        "There are errors that should be fixed before saving.",
      ]);
    }
    setLoading(false);
  }, [
    auth,
    fileResources,
    initialSubmission,
    onCancel,
    showAlert,
    showConfirm,
    submission,
    uploadUserSubmission,
  ]);

  const handleCloseClicked = useCallback(() => {
    showConfirm(
      "Your upload won't be saved",
      "Do you wish to discard this upload and go back to organizing?",
      {
        onConfirm: () => {
          onCancel();
          setSubmission(initialSubmission);
          setInitialSubmission(initialSubmission);
        },
      },
      "Yes, Discard",
      "No, I'm not done",
    );
  }, [initialSubmission, showConfirm, onCancel]);

  return (
    <Dialog
      fullScreen
      classes={{ paper: classes.dialogRoot }}
      open={visible}
      onClose={handleCloseClicked}
    >
      {loading && <LoadingIndicator transparent />}

      <MediaContext.Provider
        value={{
          initial: initialSubmission,
          media: submission,
          setMedia: setSubmission,
          errors: errors,
          setErrors: setErrors,
          requiredFields,
          isUpload: true,
          validator: validator,
        }}
      >
        <DialogTitle
          id="upload-media-dialog-title"
          onClose={handleCloseClicked}
          type={submission.type}
        >
          Upload Media
        </DialogTitle>

        <Divider />

        <DialogContent id="upload-media" classes={{ root: classes.root }}>
          <SnackAlert handler={snackAlertHandler} />
          <ConfirmDialog handler={confirmHandler} />

          <Container
            id="upload-media-container"
            maxWidth={false}
            disableGutters
          >
            <Grid
              container
              id="upload-media-panel"
              className={classes.editPanel}
            >
              <Typography variant="h6" style={{ marginBottom: 30 }}>
                File Info
              </Typography>

              <Grid
                container
                item
                xs={12}
                component={Box}
                height="auto"
                spacing={2}
              >
                <Grid item xs={12} sm={4} component={Box} pr={"25px"}>
                  <MediaTypeSelect />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm
                  md={6}
                  container
                  className={classes.fileUploadContainer}
                >
                  {notBlank(submission.type) && (
                    <FileUpload
                      required
                      id="creator-file-upload-button"
                      error={errors?.file}
                      fileType="submission"
                      label="Upload"
                      accept={getAcceptedFileTypes(submission.type)}
                      setFileResources={setFileResources}
                      showAlert={showAlert}
                      submissionType={submission.type}
                    />
                  )}

                  {notBlank(submission.type) && submission.type !== "IMAGE" && (
                    <FileUpload
                      id="creator-cover-file-upload-button"
                      fileType="cover"
                      label="Cover Image (optional)"
                      accept={getAcceptedFileTypes("COVER")}
                      setFileResources={setFileResources}
                      showAlert={showAlert}
                      submissionType="COVER"
                    />
                  )}
                </Grid>
              </Grid>

              <Divider className={classes.divider} />

              <Grid
                item
                xs={6}
                md={4}
                className={clsx(classes.column, classes.columnLeft)}
              >
                {notBlank(submission.type) && (
                  <Fragment>
                    <Grid item>
                      <SubmissionDetails />
                    </Grid>

                    <Grid item>
                      <SubmissionInfo className={classes.info} />
                    </Grid>
                  </Fragment>
                )}
              </Grid>

              {notBlank(submission.type) && (
                <Grid
                  item
                  sm={6}
                  md={8}
                  className={clsx(classes.column, classes.columnRight)}
                >
                  <Grid item>
                    <Description
                      required={includes(
                        "description",
                        requiredFields[submission.type],
                      )}
                      error={validator.hasError(errors, "description")}
                      helperText={validator.firstError(errors, "description")}
                    />
                  </Grid>

                  <Grid container item>
                    <Grid item xs={12} md={6} style={{ paddingRight: 25 }}>
                      <Categories className={classes.categories} />
                    </Grid>

                    <Grid item xs={12} md={6} className={classes.contributors}>
                      <Contributors />
                    </Grid>

                    {includes(submission.type, ["BOOK", "AUDIOBOOK"]) && (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        className={classes.externalLinks}
                      >
                        <ExternalLinks />
                      </Grid>
                    )}

                    <Grid
                      item
                      xs={12}
                      md={6}
                      className={classes.productIdentifiers}
                    >
                      <ProductIdentifiers />
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>

            {notBlank(submission.type) && <OptionalFields />}
          </Container>
        </DialogContent>

        <Divider />

        <DialogActions classes={{ root: classes.actions }}>
          <Button onClick={handleCloseClicked} style={{ color: "black" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isBlank(submission.type)}
            onClick={handleSave}
          >
            Save
          </Button>
        </DialogActions>
      </MediaContext.Provider>
    </Dialog>
  );
};

export default UploadMediaDialog;
